import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import { Center, Spinner } from 'src/components/atoms';
import { EmptyState } from 'src/components/molecules';
import { SlideOut } from 'src/components/molecules/SlideOut/SlideOut';
import { PaginatedTable } from 'src/components/organisms';
import { useDraftReportAuditLog } from 'src/hooks/useDraftReportAuditLog';
import { getOldAuditLogEntryProps } from 'src/utils/getOldAuditLogEntryProps';

const formatTimestamp = (date: Date): string =>
  dayjs(date).format('YYYY.MM.DD HH:mm');

const ENTRIES_PER_PAGE = 10;
const COLUMNS = [{ label: 'Timestamp' }, { label: 'Event' }, { label: 'User' }];

interface DraftCaseAuditLogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  reportID: string;
}

export const DraftCaseAuditLog = ({
  isOpen,
  setIsOpen,
  reportID,
}: DraftCaseAuditLogProps) => {
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const { auditLog, isLoading } = useDraftReportAuditLog(reportID);

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
    }
  }, [isOpen, reportID]);

  const handleClose = () => {
    setIsOpen(false);
    setCurrentPage(1);
  };

  const numPages = Math.ceil(auditLog.length / ENTRIES_PER_PAGE) || 1;
  const indexOfLastEntry = currentPage * ENTRIES_PER_PAGE;
  const indexOfFirstEntry = indexOfLastEntry - ENTRIES_PER_PAGE;
  const currentEntries = auditLog.slice(indexOfFirstEntry, indexOfLastEntry);

  const tableData = currentEntries.map((entry) => {
    const { children } = getOldAuditLogEntryProps(entry, false, theme);
    return {
      cellData: [
        formatTimestamp(new Date(entry.createdAt ?? '')),
        children,
        entry.user?.name ?? '-',
      ],
      row: entry.id,
    };
  });

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const showEmptyState =
    !isLoading && (reportID === 'new' || auditLog.length === 0);
  const customEmptyState = isLoading ? (
    <Spinner variant="secondary" size="large" />
  ) : (
    <EmptyState
      title="No audit logs yet"
      description="There are no audit logs yet for this draft"
      notificationEmptyState
    />
  );

  return (
    <SlideOut
      isOpen={isOpen}
      title="Audit log"
      onClose={handleClose}
      onConfirm={handleClose}
      confirmButtonText="Close"
      confirmButtonDataTestid="draft-case-audit-log-slideout-close"
      applyButtonDisabled={false}
      showFooter={false}
      containerDataTestid="draft-case-audit-log-slideout"
      bodyRevealDelayMs={0}
      headerRevealDelayMs={0}
    >
      <div data-testid="draft-case-audit-log-content">
        {showEmptyState ? (
          <Center style={{ minHeight: 300 }}>{customEmptyState}</Center>
        ) : (
          <PaginatedTable
            columns={COLUMNS}
            tableData={tableData}
            numPages={numPages}
            currentPage={currentPage}
            handleClickPage={handlePageChange}
            showPagination={numPages > 1}
            visibleColumns={COLUMNS}
            sortableColumns={[]}
            data-testid="draft-case-audit-log-table"
            fixedRowHeight={true}
            isFullWidth={true}
            emptyText="No audit log entries."
            customEmptyState={customEmptyState}
          />
        )}
      </div>
    </SlideOut>
  );
};
